import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useAppSelector } from 'store'
import { LeadState } from 'store/types'
import { LeadAdditionalOwner, LeadDetail } from 'store/types/lead'
import { LeadViewType } from 'utils/constants'
import leadQuery from 'queries/lead'
import { AppQueryOptions } from 'hooks/use-app-query'
import useLeadTab from 'queries/lead-tab'

import useUser from './use-user'
import useUserPermissions from './use-user-permissions'
import useLeadData, { useInvalidateLeadData } from './use-lead-data'
import { useLeadAccessible } from './use-lead-accessible'
import { useQueryClient } from '@tanstack/react-query'
import { leadKey } from 'utils/constants/queryKey'
import { LEAD_ROUTE_V2 } from 'utils/constants/url'

export type Values = {
  camsUsername: string | null
  collaboratorsIds: string[] | null
  hasCallTask: boolean
  isReadOnly: boolean
  isRecycled: boolean
  leadId?: string
  ownerTeamId: string | null
  ownerId: string | null
  dispatch: LeadDetail['dispatch']
  //
  // Delete info
  deletedBy: string | null
  deletedByUser: string | null
  deletedTs: string | null
  isDeleted: boolean
  //
  // Lead queue
  isDispatched: boolean
  //
  // Lead status
  isLeadInvalid: boolean
  isLeadLost: boolean
  isLeadWon: boolean
  hasOpenOpportunity: boolean
  canAssignLeadToOthers: boolean
  canAssignLeadToSelf: boolean
}

export const resolveLeadDetailStatus = (leadDetail: LeadDetail | undefined, leadTasks: LeadState['leadTasks']) => {
  const result: Values = {
    camsUsername: null,
    collaboratorsIds: null,
    deletedBy: null,
    deletedByUser: null,
    deletedTs: null,
    dispatch: undefined,
    isDispatched: false,
    isDeleted: true,
    isLeadInvalid: false,
    isLeadLost: false,
    isLeadWon: false,
    isReadOnly: true,
    isRecycled: false,
    leadId: undefined,
    ownerTeamId: null,
    ownerId: null,
    hasCallTask: false,
    hasOpenOpportunity: false,
    canAssignLeadToOthers: false,
    canAssignLeadToSelf: false,
  }

  if (!leadDetail) {
    return result
  }

  const {
    additionalOwners,
    camsUsername,
    deletedBy,
    deletedByUser,
    deletedTs,
    dispatch,
    isDeleted,
    isRecycled,
    owner,
    ownerTeam,
    opportunityStatusCount,
  } = leadDetail

  result.camsUsername = camsUsername
  result.collaboratorsIds = additionalOwners.map((i: LeadAdditionalOwner) => i.id)

  // Lead status
  result.isLeadInvalid = leadDetail.viewType === LeadViewType.invalid
  result.isLeadLost = leadDetail.viewType === LeadViewType.lost
  result.isLeadWon = leadDetail.viewType === LeadViewType.converted

  result.ownerTeamId = ownerTeam?.id || null
  result.ownerId = owner?.id || null

  result.deletedBy = deletedBy
  result.deletedByUser = deletedByUser
  result.deletedTs = deletedTs
  result.isDeleted = isDeleted

  result.isReadOnly = result.isLeadInvalid || result.isLeadLost || result.isDeleted
  result.isRecycled = isRecycled

  result.isDispatched = Boolean(dispatch?.userId)

  result.leadId = leadDetail.leadId

  result.hasCallTask = !!leadTasks.data?.some((t) => Boolean(t.isCallback || t.isFirstCall))

  result.hasOpenOpportunity = opportunityStatusCount?.some((o) => ['open', 'trial'].includes(o.status)) || false

  result.canAssignLeadToOthers = leadDetail.canAssignLeadToOthers
  result.canAssignLeadToSelf = leadDetail.canAssignLeadToSelf

  return result
}

interface Props {
  queryOptions?: AppQueryOptions
  leadId?: string
}

const useLeadDetail = (props?: Props) => {
  const { t } = useTranslation()
  const path = window.location.pathname
  let leadIdParam
  if (path.includes(LEAD_ROUTE_V2)) {
    leadIdParam = path.match(/\/lead\/v2\/([^/]+)/)?.[1] ?? ''
  } else {
    leadIdParam = path.match(/\/lead\/([^/]+)/)?.[1] ?? ''
  }

  const leadId = props?.leadId || leadIdParam

  const { currentUser } = useUser()
  const [currentUserPermissions] = useUserPermissions('accountWorkflow')
  const { leadTasks } = useAppSelector((state) => state.lead)
  const queryClient = useQueryClient()

  const { isLoading, isSuccess, isError, error, isFetching, refetch } = useLeadData({
    leadId,
    queryOptions: props?.queryOptions,
  })

  const invalidateItem = () => {
    queryClient.invalidateQueries(leadKey.data(leadId))
    queryClient.invalidateQueries(leadKey.detail(leadId))
  }

  const { data } = leadQuery.useQueryDetail(leadId, {
    enabled: false,
  })

  const leadTabQueryResult = useLeadTab(leadId, {
    enabled: false,
  })

  const values = useMemo(() => resolveLeadDetailStatus(data, leadTasks), [data, leadTasks])

  const isLeadAssigned = Boolean(values.ownerId || values.ownerTeamId)
  const isDispatchedToCurrentUser = values.dispatch?.userId === currentUser.id

  const { isCustodian: isCurrentUserLeadCustodian, canEdit, isTeamMembersInCollaborator } = useLeadAccessible({
    leadOwner: { leadOwnerId: values.ownerId, leadOwnerTeamId: values.ownerTeamId },
    collaborators: values.collaboratorsIds,
    isQueuedForUser: isDispatchedToCurrentUser,
  })

  const canUserEditLeadInfo = useMemo(() => {
    if (values.isDeleted) {
      return false
    }
    const isLostInvalid = values.isLeadLost || values.isLeadInvalid
    if (isLostInvalid) {
      const { canEditLostInvalidLeads: canEditLostInvalidLeadsPermission = false } = currentUserPermissions
      return isLostInvalid && canEditLostInvalidLeadsPermission
    }
    return canEdit
  }, [values.isDeleted, values.isLeadLost, values.isLeadInvalid, canEdit, currentUserPermissions])

  return {
    ...values,
    refetch,
    leadId,
    isDispatchedToCurrentUser,
    isLeadOwner: values.ownerId === currentUser.id,
    // TODO: replace `isOwner` implementation with `canUserEditLeadInfo`
    isOwner:
      isCurrentUserLeadCustodian ||
      // adding these back to allow edit via EDIT_OTHER_ACCOUNT permission; also add EDIT_UNASSIGNED_LEAD temporarily
      currentUserPermissions.canEditOtherAccount ||
      currentUserPermissions.canEditUnassignedLead,
    isTeamMembersInCollaborator,
    canUserEditLeadInfo,
    // allow delete if user has edit permission
    canDeleteDocument: !isLeadAssigned
      ? false
      : isCurrentUserLeadCustodian
      ? currentUserPermissions.canEdit
      : currentUserPermissions.canEditOtherAccount,
    // allow upload if user has view or edit permission
    canUploadDocument: !isLeadAssigned
      ? false
      : isCurrentUserLeadCustodian
      ? currentUserPermissions.canView || currentUserPermissions.canEdit
      : currentUserPermissions.canViewOtherAccount || currentUserPermissions.canEditOtherAccount,
    isLoading,
    isSuccess,
    isError,
    isFetching,
    error: leadId
      ? error
      : {
          name: t('error_not_found', { name: 'Lead ID' }),
          code: -1,
          message: t('error_not_found', { name: 'Lead ID' }),
        },
    data,
    invalidateItem,
    leadTab: leadTabQueryResult.data,
    leadTabQueryResult,
  }
}

export const useInvalidateLeadDetail = (leadId: string) => useInvalidateLeadData(leadId)

export default useLeadDetail
